import type { PostHogConfig } from 'posthog-js'

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN?.trim() || ''
const posthogHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() || 'https://eu.credem.streetbeat.com'
const posthogUiHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST?.trim() || 'https://eu.posthog.com'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const OPAQUE_ID_RE = /^(?:[A-Z]{1,4}\d{2,}|[A-Za-z0-9_-]{10,})$/

export interface AnalyticsRoute {
  path: string
  route_view: string
  route_has_client: boolean
  route_has_product: boolean
  route_has_report: boolean
  route_has_thread: boolean
  route_is_admin: boolean
}

function redactUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl)
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return rawUrl.split('?')[0]?.split('#')[0] || rawUrl
  }
}

function sanitizeSegment(segment: string, previous?: string): string {
  if (previous === 'clients') return ':clientCode'
  if (previous === 'products') return ':productId'
  if (previous === 'reports') return ':reportId'
  if (previous === 'threads' || previous === 'chat') return ':threadId'
  if (previous === 'advisors') return ':advisorId'
  if (UUID_RE.test(segment)) return ':id'
  if (/^\d+$/.test(segment)) return ':id'
  if (OPAQUE_ID_RE.test(segment) && segment !== segment.toLowerCase()) return ':id'
  return segment
}

export function analyticsRouteFor(pathname: string, search = ''): AnalyticsRoute {
  const rawSegments = pathname.split('/').filter(Boolean)
  const segments = rawSegments.map((segment, index) =>
    sanitizeSegment(segment, rawSegments[index - 1]),
  )
  const normalizedPath = `/${segments.join('/')}`.replace(/\/+$/, '') || '/'
  const searchParams = new URLSearchParams(search)
  const hasThread = rawSegments.includes('chat') || Boolean(searchParams.get('thread_id'))

  return {
    path: normalizedPath,
    route_view: rawSegments[0] || 'home',
    route_has_client: rawSegments[0] === 'clients' && rawSegments.length > 1,
    route_has_product: rawSegments[0] === 'products' && rawSegments.length > 1,
    route_has_report: rawSegments[0] === 'reports' && rawSegments.length > 1,
    route_has_thread: hasThread,
    route_is_admin: rawSegments[0] === 'admin',
  }
}

export const isPostHogEnabled = Boolean(posthogToken)
export const posthogApiKey = isPostHogEnabled ? posthogToken : null
export const posthogOptions: Partial<PostHogConfig> = {
  api_host: posthogHost,
  ui_host: posthogUiHost,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_performance: false,
  defaults: '2026-01-30',
  disable_session_recording: false,
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
    maskCapturedNetworkRequestFn: (request) => {
      request.name = redactUrl(request.name)
      request.requestHeaders = undefined
      request.responseHeaders = undefined
      request.requestBody = undefined
      request.responseBody = undefined
      return request
    },
  },
}
